import { Button, PasswordInput, PinInput } from '@mantine/core';
import React from 'react';
import styled from 'styled-components';
import { Colors } from '../patterns/Colors';

const CODE_LENGTH = 6;

interface Props {
    email: string;
    code: string;
    password: string;
    onCodeChange: (code: string) => void;
    onPasswordChange: (password: string) => void;
    onSubmit: () => void;
    onResend: () => void;
    resetting: boolean;
    resending: boolean;
    error?: string | null;
}

export const ResetPasswordForm = ({ email, code, password, onCodeChange, onPasswordChange, onSubmit, onResend, resetting, resending, error }: Props) => {

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (code.length < CODE_LENGTH || !password || resetting) return;
        onSubmit();
    };

    const handleResend = (e: React.MouseEvent) => {
        e.preventDefault();
        if (resending) return;
        onResend();
    };

    return <Form onSubmit={handleSubmit}>
        <Intro>
            We sent a 6-digit reset code to <strong>{email}</strong>. Enter it with your new password below.
        </Intro>
        <PinInputWrap>
            <PinInput
                length={CODE_LENGTH}
                type="number"
                inputMode="numeric"
                oneTimeCode
                autoFocus
                value={code}
                onChange={onCodeChange}
                disabled={resetting}
                aria-label="Reset code"
            />
        </PinInputWrap>
        <PasswordInput
            placeholder="Your new password"
            value={password}
            onChange={e => onPasswordChange(e.currentTarget.value)}
            autoComplete="new-password"
            aria-label="New password"
            disabled={resetting}
            w="100%"
            mb="md"
        />
        <Button
            type="submit"
            loading={resetting}
            color="primary"
            size="sm"
            fullWidth
        >
            Set password and sign in
        </Button>
        {error && <ErrorText>{error}</ErrorText>}
        <Resend>
            Didn&apos;t get it?{' '}
            <ResendLink href="#" onClick={handleResend} aria-disabled={resending}>
                {resending ? 'Sending…' : 'Send a new code.'}
            </ResendLink>
        </Resend>
    </Form>;
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const Intro = styled.p`
    font-size: 0.875rem;
    color: ${Colors.text};
    margin: 0 0 1rem;
`;

const PinInputWrap = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
`;

const ErrorText = styled.p`
    font-size: 0.8125rem;
    color: #b91c1c;
    margin: 0.75rem 0 0;
    text-align: center;
`;

const Resend = styled.p`
    font-size: 0.875rem;
    color: ${Colors.text};
    margin: 1rem 0 0;
    text-align: center;
`;

const ResendLink = styled.a`
    color: ${Colors.primary};
    text-decoration: none;
    font-weight: 500;

    &:hover {
        text-decoration: underline;
    }

    &[aria-disabled="true"] {
        pointer-events: none;
        opacity: 0.6;
    }
`;
